'use client';

import { Button } from '@shared/components/shadcn';
import { createDebug } from '@shared/debug';
import { ChevronLeft } from '@shared/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type FieldPath, type FieldValues, useFormContext } from 'react-hook-form';
import type { z } from 'zod';

import { MultistepFormProgressBar } from './components/MultistepFormProgressBar';
import { MultistepFormProvider } from './context/MultistepFormContext';
import { useStepChangeNotifier } from './hooks/useStepChangeNotifier';
import type { MultistepFormContextType, MultistepFormProps, Step } from './types';

const debug = createDebug({ isEnabled: false });

/**
 * Type definition for a component with validateAndUpdateData static method
 */
type StepComponentWithValidation = {
  type: {
    validateAndUpdateData?: (formMethods: ReturnType<typeof useFormContext>) => Promise<boolean>;
  };
  props: Record<string, unknown>;
};

/**
 * A flexible multistep form component that manages step navigation and progress.
 *
 * @deprecated This component includes opinionated presentational markup that may conflict with custom layouts.
 * For new implementations, consider using `MultistepFormHeadless` which provides full control over layout and styling
 * while maintaining all the multistep form logic and state management.
 *
 * @see useMultistepFormState for direct hook access
 *
 * This component will continue to be supported for backward compatibility but is not recommended for new features.
 */
function resolveInitialStepIndex(steps: Step<z.ZodType>[], initialStatus: string, initialStepId: string, formData: Record<string, unknown> = {}): number {
  const visibleSteps = steps.filter(step => !step.condition || step.condition({ formData, status: initialStatus }));
  const stepIndex = visibleSteps.findIndex(step => step.id === initialStepId);

  return stepIndex === -1 ? 0 : stepIndex;
}

function MultistepForm({
  steps,
  onComplete,
  offerComponent,
  className,
  onStepChange,
  showProgressBar = true,
  showNavigation = true,
  onMultistepFormReady,
  initialStatus,
  initialStepId,
}: MultistepFormProps) {
  const [currentStepIndex, setCurrentStepIndex] = useState(() => (initialStepId ? resolveInitialStepIndex(steps, initialStatus ?? '', initialStepId) : 0));
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [formErrors, setFormErrors] = useState<boolean>(false);
  const [status, setStatus] = useState<string>(initialStatus ?? '');
  const [pendingStepId, setPendingStepId] = useState<string | null>(null);
  const previousStepIndexRef = useRef<number>(0);
  const isNavigatingBackwardsRef = useRef<boolean>(false);

  // Access React Hook Form methods
  const formMethods = useFormContext();

  // Filter steps based on conditions - memoized to prevent unnecessary recalculations
  const visibleSteps = useMemo(() => steps.filter(step => !step.condition || step.condition({ formData, status })), [steps, formData, status]);

  // Ensure currentStepIndex is valid when visibleSteps changes
  useEffect(() => {
    // If currentStepIndex is out of bounds or the current step is no longer visible
    if (currentStepIndex >= visibleSteps.length || !visibleSteps[currentStepIndex]) {
      // Find the closest valid step index
      let newIndex = 0;
      if (visibleSteps.length > 0) {
        // Try to find the step that was at the current index in the original steps array
        const currentStepId = steps[currentStepIndex]?.id;
        if (currentStepId) {
          const foundIndex = visibleSteps.findIndex(step => step.id === currentStepId);
          if (foundIndex !== -1) {
            newIndex = foundIndex;
          } else {
            // If the current step is not found, go to the last valid step or 0
            newIndex = Math.min(currentStepIndex, visibleSteps.length - 1);
          }
        }
      }

      if (newIndex !== currentStepIndex) {
        setCurrentStepIndex(newIndex);
      }
    }
  }, [visibleSteps, currentStepIndex, steps]);

  // Use the custom hook for step change notifications
  useStepChangeNotifier(currentStepIndex, visibleSteps, onStepChange);

  // Update the previous step index ref whenever currentStepIndex changes
  useEffect(() => {
    previousStepIndexRef.current = currentStepIndex;
  }, [currentStepIndex]);

  // Effect to handle step navigation when status changes
  useEffect(() => {
    if (pendingStepId) {
      const stepIndex = visibleSteps.findIndex(step => step.id === pendingStepId);
      if (stepIndex !== -1) {
        // Clear any existing form errors when navigating
        formMethods?.clearErrors();
        setCurrentStepIndex(stepIndex);
        setFormErrors(false);
      }
      setPendingStepId(null);
    }
  }, [pendingStepId, visibleSteps, formMethods]);

  // Effect to reset the backwards navigation flag after navigation
  useEffect(() => {
    if (isNavigatingBackwardsRef.current) {
      // Reset the flag on the next tick to allow the auto-navigation logic to run
      const timeoutId = setTimeout(() => {
        isNavigatingBackwardsRef.current = false;
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [currentStepIndex]);

  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === visibleSteps.length - 1;

  // Ensure currentStep is defined
  const currentStep = visibleSteps[currentStepIndex];
  const shouldShowNavigation = showNavigation && (currentStep?.showNavigation ?? true);

  // Extract validation logic for testability
  const isValidStepIndex = (index: number, steps: typeof visibleSteps): boolean => {
    return index >= 0 && index < steps.length && !!steps[index];
  };

  // Helper function to safely navigate to a step index
  const navigateToStep = (newIndex: number, options?: { isBackwards?: boolean }) => {
    if (isValidStepIndex(newIndex, visibleSteps)) {
      if (options?.isBackwards) {
        isNavigatingBackwardsRef.current = true;
      }
      setCurrentStepIndex(newIndex);
      setFormErrors(false);
      return true;
    }
    return false;
  };

  const goToPreviousStep = () => {
    if (!isFirstStep) {
      const newIndex = currentStepIndex - 1;
      navigateToStep(newIndex, { isBackwards: true });
    }
  };

  const goToNextStep = async () => {
    if (isLoading) return;

    try {
      setIsLoading(true);
      setFormErrors(false);
      isNavigatingBackwardsRef.current = false;

      // Get the current step component safely
      const currentStep = visibleSteps[currentStepIndex];
      const currentStepComponent = currentStep?.component as StepComponentWithValidation;

      // Check if we're in Storybook context
      if (!formMethods) {
        // In Storybook, just move to the next step
        if (isLastStep) {
          onComplete?.(formData);
        } else {
          const newIndex = currentStepIndex + 1;
          navigateToStep(newIndex);
        }
        return;
      }

      // Check if the step component has a validateAndUpdateData static method
      if (currentStepComponent.type && typeof currentStepComponent.type.validateAndUpdateData === 'function') {
        // Use the component's validation method
        const isValid = await currentStepComponent.type.validateAndUpdateData(formMethods);
        if (!isValid) {
          setFormErrors(true);
          return;
        }
      } else {
        // Fallback to default validation using the already validated currentStep
        const currentStepId = currentStep?.id;
        // Type assertion to FieldPath<FieldValues> which is what trigger expects
        const isValid = await formMethods.trigger(currentStepId as FieldPath<FieldValues>, { shouldFocus: true });
        if (!isValid) {
          setFormErrors(true);
          return;
        }
      }

      if (isLastStep) {
        // For the last step, validate the entire form before submitting
        const isFormValid = await formMethods.trigger();
        if (isFormValid) {
          onComplete?.(formData);
        } else {
          setFormErrors(true);
        }
      } else {
        const newIndex = currentStepIndex + 1;
        navigateToStep(newIndex);
      }
    } catch (error) {
      debug.forceError('Error in goToNextStep:', error);
      setFormErrors(true);
    } finally {
      setIsLoading(false);
    }
  };

  const goToStep = (index: number) => {
    navigateToStep(index);
  };

  const skipToStep = useCallback(
    (stepId: string) => {
      // Clear any existing form errors when skipping steps
      formMethods?.clearErrors();
      // Set the pending step ID, which will be processed in the useEffect
      isNavigatingBackwardsRef.current = false;
      setPendingStepId(stepId);
    },
    [formMethods]
  );

  const updateFormData = (stepId: string, data: unknown) => {
    setFormData(prev => ({
      ...prev,
      [stepId]: data,
    }));
  };

  // Effect to call onMultistepFormReady callback with skipToStep and setStatus functions
  const onMultistepFormReadyCalledRef = useRef(false);
  useEffect(() => {
    if (onMultistepFormReady && !onMultistepFormReadyCalledRef.current) {
      onMultistepFormReadyCalledRef.current = true;
      onMultistepFormReady(skipToStep, setStatus);
    }
  }, [onMultistepFormReady, skipToStep, setStatus]);

  const contextValue: MultistepFormContextType = {
    currentStepIndex,
    steps: visibleSteps,
    isFirstStep,
    isLastStep,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    skipToStep,
    isLoading,
    setIsLoading,
    formData,
    updateFormData,
    formErrors,
    setStatus,
    status,
  };

  return (
    // We are rerendering the MultistepFormProvider with a unique key when the current step changes
    // to ensure that FullStory can track the data attributes correctly
    <MultistepFormProvider key={`multistep-form-${currentStepIndex}-${currentStep?.id || 'unknown'}`} value={contextValue}>
      <div className={className} data-testid="multistep-form" data-track-container={currentStep?.dataTrackContainer} data-track-step={currentStep?.dataTrackStep}>
        <header className="w-full">
          {/* Back button and offer/step title - only show when not on first step */}
          {!isFirstStep && shouldShowNavigation && (
            <nav className="flex items-center justify-between gap-12 px-5 py-3 md:hidden">
              <Button
                variant="secondary"
                size="s"
                onClick={goToPreviousStep}
                className="justify-self-start p-0 shrink-0 hover:bg-transparent w-8 h-8"
                data-testid="multistep-form-back-button"
                data-track-name="Back"
              >
                <ChevronLeft size="small" />
              </Button>
              {currentStep?.navTitle ? (
                <span className="text-sm font-medium" data-testid="multistep-form-nav-title">
                  {currentStep.navTitle}
                </span>
              ) : (
                offerComponent
              )}
              {/* Spacer to keep nav content centred */}
              <span className="w-8 h-8"></span>
            </nav>
          )}

          {/* Progress bar - always show */}
          {showProgressBar && <MultistepFormProgressBar currentStep={currentStepIndex} totalSteps={visibleSteps.length} className="bg-accent-green" />}
        </header>

        {/* Step content */}
        <section className="flex flex-col flex-1 h-full w-full justify-start" data-testid={`step-${currentStepIndex + 1}-wrapper`}>
          {currentStep?.component}
        </section>
      </div>
    </MultistepFormProvider>
  );
}

// Export validation function for testing
const isValidStepIndex = (index: number, steps: unknown[]): boolean => {
  return index >= 0 && index < steps.length && !!steps[index];
};

export { isValidStepIndex, MultistepForm, resolveInitialStepIndex };
